/**
 * Turns a catalogue row's question/answer pairs into job-form state (ticket #5616).
 *
 * A skipped step is pre-answered, not omitted: each pair becomes both
 *   - an entry in the form's `questions` map — the very same value the question's own control
 *     would have written, so the posted job carries the answer set it would have had if the
 *     user had clicked through; and
 *   - an entry in the questions-step history, which is what makes the questions step land past
 *     the pre-answered question instead of asking it again.
 *
 * Every pair is validated against the loaded category form, and the chain stops at the first
 * pair that does not check out, so stale or unrecognised ids degrade to the next-coarsest skip
 * rather than erroring or blocking the form. A pair is usable only when
 *   1. the category form has a question with that id,
 *   2. that question is a choice question (a text/date question has no id to pre-answer with),
 *   3. it is not a call-out-fee question (those belong to the separate diagnosis step),
 *   4. the answer id is one of that question's choices, and
 *   5. the question is actually reachable given the answers accumulated so far — so a detail
 *      whose question is hidden by the service answer is asked normally instead.
 */

import { AttributeModelDataType, CategoryAttribute, CategoryForm } from 'Douleutaras/redux/data/v2/categoryForm/types';
import { JobFormQuestionAnswer } from 'Douleutaras/views/pro-profile/components/ServicesCatalogue/types';

import { decideNextQuestion, filterCallOutFee } from '../hooks/questions-step';

/** The form's `questions` value shape: a choice id, or a list of them for a multi-choice question. */
export type PreAnsweredQuestions = Record<string, string | Array<string>>;

/** Pre-answers to seed the form with, plus the questions-step history that hides them. */
export interface CatalogueSkip {
  /** Keyed by question (attribute) id, exactly like the form's own `questions` values. */
  questions: PreAnsweredQuestions;
  /** Category-attribute ids of the pre-answered questions, in ask order. */
  history: Array<number>;
}

const CHOICE_DATA_TYPES: Array<AttributeModelDataType> = [
  AttributeModelDataType.CHOICE,
  AttributeModelDataType.CHOICES
];

/** The question a pair addresses, or `undefined` when nothing in the form can carry it. */
const findPreAnswerableQuestion = (
  questions: Array<CategoryAttribute>,
  pair: JobFormQuestionAnswer
): CategoryAttribute | undefined => {
  const question = questions.find(candidate => candidate.attribute?.id === pair.questionId);

  if (!question || !CHOICE_DATA_TYPES.includes(question.attribute.datatype)) {
    return undefined;
  }

  // Call-out-fee questions are asked by the diagnosis step, which keeps its own history.
  if (!filterCallOutFee({ include: false })(question)) {
    return undefined;
  }

  return question.attribute.choices?.some(choice => choice.id === pair.answerId) ? question : undefined;
};

/**
 * Resolves `pairs` against `categoryForm`. Returns the longest prefix of the chain that the form
 * can actually carry — an empty skip when the form is not loaded yet or nothing resolves.
 */
export const resolveCatalogueSkip = (
  categoryForm: CategoryForm | undefined,
  pairs: Array<JobFormQuestionAnswer> = []
): CatalogueSkip => {
  const formQuestions = categoryForm?.attributes;
  const questions: PreAnsweredQuestions = {};
  const history: Array<number> = [];

  if (!formQuestions?.length) {
    return { questions, history };
  }

  for (const pair of pairs) {
    const question = findPreAnswerableQuestion(formQuestions, pair);

    // Stop at the first unusable pair — a deeper answer is conditioned on the shallower one —
    // and only pre-answer a question the user would have been asked at this point in the chain.
    if (!question || !decideNextQuestion({ userSelectedQuestions: questions, question })) {
      break;
    }

    questions[question.attribute.id] = question.attribute.datatype === AttributeModelDataType.CHOICES
      ? [ String(pair.answerId) ]
      : String(pair.answerId);
    history.push(question.id);
  }

  return { questions, history };
};
